import mime from 'mime';
import type { Field } from 'components/user/DummyHelios/Uploader/UploadFile';
import { getBaseUrl } from 'utils/baseUrl';
import { isRecord } from 'utils/typeChecks';

const normalizeFileType = (fileType: string) => fileType.toLowerCase().replace(/^\./, '');

const getFileIconExtension = (fileType: string | undefined, fileName: string): string =>
  fileType ? normalizeFileType(fileType) : splitFileNameAndExtension(fileName).extension.replace(/^\./, '').toLowerCase();

const splitFileNameAndExtension = (fullName: string) => {
  const trimmed = fullName.trim();

  if (trimmed.startsWith('.') && trimmed.indexOf('.', 1) === -1) {
    return {
      name: trimmed,
      extension: '',
    };
  }

  const dotIndex = trimmed.lastIndexOf('.');
  if (dotIndex <= 0) {
    return {
      name: trimmed,
      extension: '',
    };
  }

  return {
    name: trimmed.slice(0, dotIndex),
    extension: trimmed.slice(dotIndex),
  };
};

const getDisplayFileName = (fileName: string) => {
  const trimmed = fileName.trim();
  if (!trimmed) return '';

  const normalizedPath = trimmed.replace(/\\/g, '/');
  const lastSegment = normalizedPath.split('/').filter(Boolean).pop() ?? trimmed;
  return lastSegment;
};

const getDownloadFileName = (fileName: string, extension: string): string => {
  if (!fileName) return fileName;
  if (checkIfFileNameHasExtension(fileName)) return fileName;

  const cleanExtension = (extension || '').replace(/^\./, '');
  return cleanExtension ? `${fileName}.${cleanExtension}` : fileName;
};

const getHeliosFileExtensionAndUrl = (file: Field, agendaId: string) => {
  const extension = file.extension ? file.extension : splitFileNameAndExtension(file.name).extension;
  const url = file.url ? file.url : getBaseUrl(`/api/helios/Files/DownloadFile?templateId=${agendaId}&fileId=${file.fileId}`);

  return { extension, url };
};

const checkIfFileNameHasExtension = (fileName: string) => /\.[^./\\]+$/.test(fileName);

const INVALID_FILE_NAME_CHAR_SET = new Set(['<', '>', ':', '"', '/', '\\', '|', '?', '*']);

const sanitizeFileName = (value: string) =>
  value
    .split('')
    .map((char) => {
      const charCode = char.charCodeAt(0);
      return charCode < 32 || INVALID_FILE_NAME_CHAR_SET.has(char) ? '_' : char;
    })
    .join('')
    .trim();

const getExtensionFromMimeType = (mimeType?: string): string => {
  const extensionFromMime = mimeType ? mime.getExtension(mimeType) : undefined;
  const extension = extensionFromMime ?? 'txt';
  return extension;
};

const ensureFileNameHasExtension = (fileName: string, mimeType?: string) =>
  checkIfFileNameHasExtension(fileName) ? fileName : `${fileName}.${getExtensionFromMimeType(mimeType)}`;

const normalizeUploadName = (name: string) => name.normalize('NFC');

const MAX_UPLOAD_FILE_SIZE_MB = 30;
const MAX_UPLOAD_FILE_SIZE_BYTES = MAX_UPLOAD_FILE_SIZE_MB * 1_000_000;
const MAX_FILES_PER_UPLOAD = 3;

const getFileUploadError = (files: readonly Pick<File, 'size'>[], maxFilesPerUpload = MAX_FILES_PER_UPLOAD, isUploading = false) =>
  files.length === 0
    ? undefined
    : isUploading
      ? 'user.detail.file.uploadInProgress'
      : files.length > maxFilesPerUpload
        ? 'user.detail.file.maxFilesPerUpload'
        : files.some((file) => file.size > MAX_UPLOAD_FILE_SIZE_BYTES)
          ? 'user.detail.file.fileTooLarge'
          : undefined;

const getFileIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => {
      if (isRecord(item)) {
        const fileId = item['fileId'];
        if (typeof fileId === 'string') return fileId;
      }

      return typeof item === 'string' ? item : undefined;
    })
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
};

const areFileIdsEqual = (current: unknown, initial: unknown): boolean => {
  const currentIds = getFileIds(current).slice().sort();
  const initialIds = getFileIds(initial).slice().sort();

  if (currentIds.length !== initialIds.length) return false;

  return currentIds.every((id, index) => id === initialIds[index]);
};

export {
  normalizeFileType,
  getFileIconExtension,
  splitFileNameAndExtension,
  getDisplayFileName,
  getDownloadFileName,
  getHeliosFileExtensionAndUrl,
  checkIfFileNameHasExtension,
  sanitizeFileName,
  getExtensionFromMimeType,
  ensureFileNameHasExtension,
  normalizeUploadName,
  MAX_UPLOAD_FILE_SIZE_MB,
  MAX_UPLOAD_FILE_SIZE_BYTES,
  MAX_FILES_PER_UPLOAD,
  getFileUploadError,
  areFileIdsEqual,
  INVALID_FILE_NAME_CHAR_SET,
};
